import React, { useEffect, useRef } from 'react';
import { Transition, TransitionChild } from '@headlessui/react';
import { Backdrop, useMediaQuery, useTheme } from '@mui/material';
import classNames from 'classnames';
import useOnClickOutside from 'helpers/hooks/useOnClickOutside';
import useScrollBlock from 'helpers/hooks/useScrollBlock';

type Direction = 'left' | 'top' | 'right' | 'bottom';
export interface DrawerProps {
  className?: string;
  isOpen: boolean;
  desktopDirection?: Direction;
  blockScrolling?: boolean;
  onClose?: () => void;
  disableOutsideClick?: boolean; //can not be closed via outside click
  mobileDirection?: Direction;
  fullScreen?: boolean;
}

const Drawer = ({
  className = '',
  isOpen,
  desktopDirection = 'right',
  mobileDirection = 'bottom',
  blockScrolling = true,
  onClose,
  children,
  disableOutsideClick = false,
  fullScreen = false,
}: React.PropsWithChildren<DrawerProps>) => {
  const { blockScroll } = useScrollBlock();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const direction = isMobile ? mobileDirection : desktopDirection;

  useEffect(() => {
    if (blockScrolling) {
      blockScroll(isOpen);
    }
    return () => blockScroll(false);
  }, [isOpen, blockScroll, blockScrolling]);

  const ref = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, () => {
    if (isOpen && onClose && !disableOutsideClick) {
      onClose();
    }
  });

  const directionStyles = {
    left: 'left-0 top-0 h-full',
    top: 'left-0 top-0 w-full',
    right: 'right-0 top-0 h-full',
    bottom: 'left-0 bottom-0 w-full',
  };

  const drawerTransitions = {
    left: {
      enterFrom: '-translate-x-full transform opacity-0',
      enterTo: 'translate-x-0 opacity-100 transition',
      leaveFrom: 'translate-x-0 opacity-100 transition',
      leaveTo: '-translate-x-full opacity-0 transition',
    },
    top: {
      enterFrom: '-translate-y-full transform opacity-0',
      enterTo: 'translate-y-0 opacity-100 transition',
      leaveFrom: 'translate-y-0 opacity-100 transition',
      leaveTo: '-translate-y-full opacity-0 transition',
    },
    right: {
      enterFrom: 'translate-x-full transform opacity-0',
      enterTo: '-translate-x-0 opacity-100 transition',
      leaveFrom: '-translate-x-0 opacity-100 transition',
      leaveTo: 'translate-x-full opacity-0 transition',
    },
    bottom: {
      enterFrom: 'translate-y-full transform opacity-0',
      enterTo: '-translate-y-0 opacity-100 transition',
      leaveFrom: '-translate-y-0 opacity-100 transition',
      leaveTo: 'translate-y-full opacity-0 transition',
    },
  };

  return (
    <Backdrop sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1 })} open={isOpen}>
      <Transition show={isOpen}>
        <TransitionChild
          as="div"
          enter="ease-out"
          enterFrom={drawerTransitions[direction].enterFrom}
          enterTo={drawerTransitions[direction].enterTo}
          leave="ease-in duration-75"
          leaveFrom={drawerTransitions[direction].leaveFrom}
          leaveTo={drawerTransitions[direction].leaveTo}
          className={classNames(
            'fixed z-[999] bg-white shadow-lg',
            { 'rounded-t-3xl': isMobile && !fullScreen },
            { 'h-[calc(100%-120px)] w-full': isMobile && !fullScreen },
            { 'w-[90%] max-w-[432px]': !isMobile },
            { 'h-full w-full': fullScreen },
            directionStyles[direction],
            className,
          )}
        >
          <div ref={ref} className={classNames('h-full')}>
            <div className="flex h-full flex-col items-stretch p-16 pt-24">{children}</div>
          </div>
        </TransitionChild>
      </Transition>
    </Backdrop>
  );
};

export default Drawer;
